import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Badge,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Divider,
  Tag,
  Avatar,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaStar,
  FaGraduationCap,
  FaWallet,
} from 'react-icons/fa';
import { useForm } from '../hooks/useForm'; // Import the custom hook

const MotionBox = motion(Box);

// Icon mapping
const iconMap = {
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaStar,
  FaGraduationCap,
  FaWallet,
};

export const HeroWithEnrollmentCard = ({ heroData, enrollmentData }) => {
  const toast = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Initialize form state using the custom hook
  // Build initial data from field definitions
  const initialFormData = enrollmentData.form.fields.reduce((acc, field) => {
    if (field.name) {
      acc[field.name] = field.defaultValue || '';
    }
    return acc;
  }, {
    course: enrollmentData.shortName || '',
    courseId: enrollmentData.courseId || '',
  });

  const {
    formData,
    handleChange: originalHandleChange,
    handleSubmit,
    isSubmitting,
    prefilledData
  } = useForm(initialFormData, 'enrollment');

  const handleChange = (e) => {
    originalHandleChange(e);
    setIsSubmitted(false);
  };

  const validatePhone = (phone) => {
    // Extract only digits from phone number (removes spaces, hyphens, country code, etc.)
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if at least 10 digits
    return digitsOnly.length >= 10;
  };

  const handleFormSubmit = async (e) => {
    try {
      // Validate phone number
      if (formData.phone && !validatePhone(formData.phone)) {
        toast({
          title: 'Invalid phone number',
          description: 'Your session details and link to join will be shared on WhatsApp.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const result = await handleSubmit(e);
      setIsSubmitted(true);

      toast({
        title: 'Enrollment submitted!',
        description: 'Your session details and link to join will be shared on WhatsApp.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // You can access the result if needed
      console.log('Form submission result:', result);

    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit enrollment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="70vh" bgGradient="linear(to-br, brand.700, brand.900)" color="white" position="relative" py={{ base: 12, md: 20 }}>
      <Container maxW="container.xl">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
          {/* Left Column - Hero Content */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge px={4} py={1} bg="accent.gold" color="brand.800" fontSize="sm" fontWeight="700" borderRadius="full">
              {heroData.badge}
            </Badge>

            <Heading as="h1" fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }} fontWeight="900" color="white" lineHeight="1.05" mt={6}>
              {heroData.title}
            </Heading>

            <Text fontSize="lg" color="whiteAlpha.800" mt={4} maxW="lg">
              {heroData.description}
            </Text>

            <HStack spacing={4} mt={6}>
              {heroData.buttons.map((button, index) => (
                <Button
                  key={index}
                  variant={button.variant}
                  size={button.size}
                  h={button.h}
                  px={button.px}
                  borderColor="accent.gold"
                  color="brand.50"
                  _hover={{ bg: 'accent.gold', color: 'brand.800' }}
                  as="a"
                  href={button.href}
                  >
                  {button.text}
                </Button>
              ))}
            </HStack>

            <HStack spacing={6} mt={8}>
              {heroData.stats.map((stat, index) => (
                <HStack key={index} spacing={2}>
                  <Icon as={iconMap[stat.icon]} boxSize={5} color="white" />
                  <Text fontWeight="700">{stat.text}</Text>
                </HStack>
              ))}
            </HStack>
          </MotionBox>

          {/* Right Column - Enrollment Card */}
          <MotionBox 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
          >
            <Box p={8} bg="white" borderRadius="2xl" boxShadow="0 12px 40px rgba(16,185,129,0.12)" maxW={{ base: 'full', md: 'md' }}>
              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" color="brand.600">{enrollmentData.track}</Text>
                  <Heading size="md" color="brand.700">{enrollmentData.shortName}</Heading>
                </VStack>

                <Tag bg="accent.gold" color="brand.800" fontWeight={700}>{enrollmentData.popularTag}</Tag>
              </HStack>

              <HStack spacing={4} mt={6} align="center">
                <Heading size="lg" color="brand.800">{enrollmentData.price}</Heading>
                <Text fontSize="sm" color="brand.500">{enrollmentData.priceNote}</Text>
              </HStack>

              <Text fontSize="sm" color="brand.500" mt={3}>
                {enrollmentData.description}
              </Text>

              <Divider my={6} />

              {/* Show prefilled data alert */}
              {prefilledData && (prefilledData.name || prefilledData.email) && (
                <Alert status="info" borderRadius="md" fontSize="sm" mb={4} color="brand.600">
                  <AlertIcon />
                  We've prefilled your information
                </Alert>
              )}

              <form onSubmit={handleFormSubmit}>
                <VStack spacing={4}>
                  {enrollmentData.form.fields.map((field, index) => {
                    const fieldName = field.name;

                    if (field.type === 'checkbox') {
                      return (
                        <Box key={index} w="full">
                          <FormControl display="flex" alignItems="flex-start" gap={3}>
                            <Checkbox
                              name={fieldName}
                              isChecked={formData[fieldName] || false}
                              onChange={handleChange}
                              colorScheme="green"
                              mt={1}
                            />
                            <VStack align="start" spacing={2}>
                              <FormLabel mb={0} color="brand.700" cursor="pointer">
                                {field.label}
                              </FormLabel>
                              {field.helperText && (
                                <Text fontSize="sm" color="brand.500">
                                  {field.helperText}
                                </Text>
                              )}
                              {formData[fieldName] && field.confirmMessage && (
                                <Alert status="info" borderRadius="md" fontSize="sm" mt={2} color="brand.500" >
                                  <AlertIcon />
                                  {field.confirmMessage}
                                </Alert>
                              )}
                            </VStack>
                          </FormControl>
                        </Box>
                      );
                    }

                    return (
                      <FormControl key={index} isRequired={field.required}>
                        <FormLabel color="brand.700">{field.label}</FormLabel>
                        <Input
                          name={fieldName}
                          type={field.type}
                          value={formData[fieldName] || field.defaultValue || ''}
                          onChange={handleChange}
                          size="lg"
                          borderColor="brand.200"
                          color="brand.800"
                          _focus={{ borderColor: 'accent.green', boxShadow: '0 0 0 1px #10B981' }}
                          placeholder={field.placeholder}
                          isReadOnly={fieldName === 'batch'}
                          bg={fieldName === 'batch' ? 'gray.50' : 'white'}
                          cursor={fieldName === 'batch' ? 'not-allowed' : 'text'}
                          pattern={fieldName === 'phone' ? '[0-9+\\s\\-()]*' : undefined}
                          title={fieldName === 'phone' ? 'Phone number must have at least 10 digits. Can include country code, spaces, and hyphens.' : undefined}
                        />
                      </FormControl>
                    );
                  })}
                  
                  {/* Hidden course fields */}
                  <input type="hidden" name="course" value={enrollmentData.shortName} />
                  <input type="hidden" name="courseId" value={enrollmentData.courseId} />

                  <Button
                    type="submit"
                    variant="solid"
                    size="lg"
                    w="full"
                    h="60px"
                    isLoading={isSubmitting}
                    loadingText="Submitting..."
                    isDisabled={isSubmitted}
                    bg={isSubmitted ? "brand.300" : "accent.green"}
                    color="white"
                    _hover={{ bg: isSubmitted ? "brand.300" : 'accent.gold', color: isSubmitted ? "white" : 'brand.800' }}
                    cursor={isSubmitted ? "not-allowed" : "pointer"}
                  >
                    {isSubmitted ? "Submitted" : enrollmentData.form.submitText}
                  </Button>
                </VStack>
              </form>
            </Box>
          </MotionBox>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export const CurriculumSection = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.xl">
        <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Heading size="2xl" fontWeight="900" color="brand.800" mb={4}>{data.title}</Heading>
          <Text color="brand.600" mb={8} maxW="2xl">{data.description}</Text>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {data.cards.map((card, index) => (
              <Box key={index} p={8} bg="white" borderRadius="2xl" boxShadow="0 4px 20px rgba(0,0,0,0.06)">
                <Icon as={iconMap[card.icon]} boxSize={12} color="accent.green" mb={4} />
                <Heading size="md" color="brand.700" mb={3}>{card.title}</Heading>
                <Text color="brand.500">{card.description}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </MotionBox>
      </Container>
    </Box>
  );
};

export const ProjectsSection = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="brand.50">
      <Container maxW="container.xl">
        <Heading size="2xl" mb={4} color="brand.800">{data.title}</Heading>
        <Text color="brand.600" mb={8}>{data.description}</Text>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {data.items.map((project, index) => (
            <Box key={index} p={6} bg="white" borderRadius="2xl">
              <Icon as={project.icon} boxSize={12} color="accent.green" mb={4} />
              <Heading size="sm" mb={2}>{project.title}</Heading>
              <Text color="brand.500">{project.description}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export const TestimonialsSection = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.xl">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} alignItems="center">
          <VStack align="start" spacing={4}>
            <Heading size="2xl" color="brand.800">{data.title}</Heading>
            <Text color="brand.600">{data.description}</Text>

            <HStack spacing={8} mt={4}>
              {data.stats.map((stat, index) => (
                <VStack key={index}>
                  <Text fontWeight={700} fontSize="2xl">{stat.value}</Text>
                  <Text fontSize="sm" color="brand.500">{stat.label}</Text>
                </VStack>
              ))}
            </HStack>
          </VStack>

          {data.testimonials.map((testimonial, index) => (
            <Box key={index} p={6} bg="white" borderRadius="2xl" boxShadow="0 4px 20px rgba(0,0,0,0.06)">
              <HStack>
                <Avatar name={testimonial.name} />
                <VStack align="start" spacing={0}>
                  <Text fontWeight={700}>{testimonial.name}</Text>
                  <Text fontSize="sm" color="brand.500">{testimonial.role}</Text>
                </VStack>
              </HStack>

              <Text mt={4} color="brand.600">{testimonial.text}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export const FAQSection = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="brand.50">
      <Container maxW="container.xl">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box>
            <Heading size="2xl" color="brand.800" mb={4}>{data.title}</Heading>

            <Accordion allowMultiple>
              {data.items.map((item, index) => (
                <AccordionItem key={index}>
                  <h3>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">{item.question}</Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h3>
                  <AccordionPanel pb={4} color="brand.600">{item.answer}</AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
};